"use client";

import {
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";

import { fetchWithAuth } from "@/lib/api";

import {
  getStoredAllowedModules,
  getStoredPermissions,
  resolveRoles,
  type ModuleKey,
} from "@/lib/module-access";

export type PermissionScope = "all" | "own";

export interface StoredRbacAccess {
  ready: boolean;
  allowedModules: ModuleKey[];
  permissions: string[];
  permissionScopes: Record<string, PermissionScope>;
  roles: string[];
  hasToken: boolean;
}

const SERVER_SNAPSHOT = "__rbac_server_snapshot__";
const RBAC_ACCESS_UPDATED_EVENT = "rbac-access-updated";

const apiBase = () =>
  process.env.NEXT_PUBLIC_API_URL || "/api";

function readBrowserSnapshot(): string {
  if (typeof window === "undefined") {
    return SERVER_SNAPSHOT;
  }

  return JSON.stringify({
    token: window.localStorage.getItem("token") || "",
    modules:
      window.localStorage.getItem("user_allowed_modules") || "",
    permissions:
      window.localStorage.getItem("rbac_permissions") || "",
    permissionScopes:
      window.localStorage.getItem("rbac_permission_scopes") || "",
    roles:
      window.localStorage.getItem("rbac_roles") || "",
    userData:
      window.localStorage.getItem("user_data") || "",
    cookie:
      typeof document === "undefined"
        ? ""
        : document.cookie,
  });
}

function subscribeRbacAccess(
  onStoreChange: () => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener("storage", onStoreChange);
  window.addEventListener(
    RBAC_ACCESS_UPDATED_EVENT,
    onStoreChange,
  );
  window.addEventListener("focus", onStoreChange);

  return () => {
    window.removeEventListener("storage", onStoreChange);
    window.removeEventListener(
      RBAC_ACCESS_UPDATED_EVENT,
      onStoreChange,
    );
    window.removeEventListener("focus", onStoreChange);
  };
}

export function emitRbacAccessChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new Event(RBAC_ACCESS_UPDATED_EVENT),
    );
  }
}

async function refreshRbacAccess() {
  if (typeof window === "undefined") return;

  const token = window.localStorage.getItem("token");

  if (!token) return;

  try {
    const response = await fetchWithAuth(
      `${apiBase()}/me`,
    );

    if (!response.ok) {
      console.error(
        "Failed to refresh RBAC access:",
        response.status,
        response.statusText,
      );

      return;
    }

    const payload = await response.json();

    // Support response langsung maupun { data: {...} }
    const user = payload?.data ?? payload;

    const allowedModules = Array.isArray(
      user.allowed_modules,
    )
      ? user.allowed_modules
      : [];

    const permissions = Array.isArray(
      user.rbac_permissions,
    )
      ? user.rbac_permissions
      : [];

    const roles = Array.isArray(user.rbac_roles)
      ? user.rbac_roles
      : [];
    const permissionScopes = normalizePermissionScopes(
      user.rbac_permission_scopes,
    );

    window.localStorage.setItem(
      "user_allowed_modules",
      JSON.stringify(allowedModules),
    );

    window.localStorage.setItem(
      "rbac_permissions",
      JSON.stringify(permissions),
    );

    window.localStorage.setItem(
      "rbac_roles",
      JSON.stringify(roles),
    );
    window.localStorage.setItem(
      "rbac_permission_scopes",
      JSON.stringify(permissionScopes),
    );

    emitRbacAccessChanged();
  } catch (error) {
    console.error(
      "Failed to refresh RBAC access:",
      error,
    );
  }
}

function normalizePermissionScopes(
  value: unknown,
): Record<string, PermissionScope> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, PermissionScope>>(
    (scopes, [permission, scope]) => {
      if (scope === "all" || scope === "own") {
        scopes[permission] = scope;
      }

      return scopes;
    },
    {},
  );
}

function getStoredPermissionScopes(): Record<string, PermissionScope> {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return normalizePermissionScopes(
      JSON.parse(
        window.localStorage.getItem("rbac_permission_scopes") || "{}",
      ),
    );
  } catch {
    return {};
  }
}

export function useStoredRbacAccess(): StoredRbacAccess {
  useEffect(() => {
    refreshRbacAccess();

    const handleFocus = () => {
      refreshRbacAccess();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const snapshot = useSyncExternalStore(
    subscribeRbacAccess,
    readBrowserSnapshot,
    () => SERVER_SNAPSHOT,
  );

  return useMemo(() => {
    if (snapshot === SERVER_SNAPSHOT) {
      return {
        ready: false,
        allowedModules: [],
        permissions: [],
        permissionScopes: {},
        roles: [],
        hasToken: false,
      };
    }

    return {
      ready: true,
      allowedModules: getStoredAllowedModules(),
      permissions: getStoredPermissions(),
      permissionScopes: getStoredPermissionScopes(),
      roles: resolveRoles(),
      hasToken: Boolean(
        window.localStorage.getItem("token"),
      ),
    };
  }, [snapshot]);
}
